/**
 * /app/settings — read-only account snapshot.
 *
 * Keep this page product-facing. The raw permission list is useful to
 * engineers, but dumping ~100 permission keys in the UI reads like a
 * debug panel and makes the MVP feel unfinished.
 */

import { Link } from 'react-router-dom';
import { useMeContext } from '../context/MeContextProvider';
import { hasRoleCategory } from '../permissions';
import { Placeholder } from '../components/states';

const ACCESS_GROUPS: ReadonlyArray<{
  readonly title: string;
  readonly description: string;
  readonly permissions: ReadonlyArray<string>;
}> = [
  {
    title: 'العملاء ومساحات العمل',
    description: 'عرض العملاء، الملفات، والمبادرات المرتبطة بهم.',
    permissions: ['client.read', 'client.manage', 'initiative.read'],
  },
  {
    title: 'المحتوى والموافقات',
    description: 'متابعة المحتوى، النسخ، ومراحل الموافقة.',
    permissions: ['content.read', 'content.create', 'approval.client.decide'],
  },
  {
    title: 'الإعلانات المدفوعة',
    description: 'قراءة الحسابات، الحملات، المجموعات، والإعلانات.',
    permissions: ['paid_media.account.read', 'paid_media.campaign.read'],
  },
  {
    title: 'المؤثرون والإبداع',
    description: 'الوصول إلى كتالوج المؤثرين ومكتبة الأصول الإبداعية.',
    permissions: ['influencer.catalog.read', 'creative.intelligence.read'],
  },
  {
    title: 'التقارير والنشر',
    description: 'قراءة التقارير، الملفات الناتجة، وحسابات النشر.',
    permissions: ['report.read', 'publish.connection.read', 'publish.log.read'],
  },
  {
    title: 'إدارة الوكالة',
    description: 'الفريق، الأدوار، المقاعد، وإعدادات المؤسسة.',
    permissions: ['team.read', 'team.manage', 'org.setting.manage', 'org.seats.manage'],
  },
];

export function SettingsPage() {
  const { me } = useMeContext();
  if (!me) return null;

  const roleLabel =
    me.roles.length > 0 ? me.roles.map((r) => roleTitle(r.key)).join('، ') : 'بدون دور';
  const permissionSet = new Set(me.permissions);
  const visibleGroups = ACCESS_GROUPS.map((group) => ({
    ...group,
    enabled: group.permissions.some((p) => permissionSet.has(p)),
  }));
  const enabledCount = visibleGroups.filter((g) => g.enabled).length;
  const isAgencyActor = hasRoleCategory(me, 'agency');

  return (
    <div className="stack-md">
      <div className="page-head">
        <div>
          <h1 className="h-page">الإعدادات</h1>
          <p className="sub">ملفك الشخصي وسياق الوكالة الحالي.</p>
        </div>
      </div>

      <section className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>الملف الشخصي</h2>
          <span className="meta">profile</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
            gap: 14,
          }}
        >
          <Field label="الاسم" value={me.profile?.full_name} />
          <Field label="البريد" value={me.profile?.email} />
          <Field label="معرّف الحساب" value={me.actor.user_id} />
          <Field label="الحالة" value={me.profile?.is_active ? 'فعّال' : 'غير فعّال'} />
        </div>
      </section>

      {isAgencyActor ? (
        <section className="card" style={{ padding: 22 }}>
          <div className="sec-title">
            <div>
              <h2>ملف الوكالة</h2>
              <p className="tiny" style={{ marginTop: 6 }}>
                المعلومات القانونية وبيانات جهة التواصل المستخدمة في مراجعة تفعيل الوكالة.
              </p>
            </div>
            <Link className="btn btn-ghost btn-sm" to="/app/onboarding">
              عرض ملف الوكالة
            </Link>
          </div>
        </section>
      ) : null}

      <section className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>المؤسسة</h2>
          <span className="meta">organization</span>
        </div>
        {me.organization ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
              gap: 14,
            }}
          >
            <Field label="الاسم" value={me.organization.name} />
            <Field label="المعرّف" value={me.organization.slug} />
            <Field label="اللغة الأساسية" value={me.locale} />
            <Field label="المنطقة الزمنية" value={me.timezone} />
          </div>
        ) : me.system_admin ? (
          <p className="body">حساب System admin — لا يوجد سياق وكالة.</p>
        ) : (
          <p className="body">لم يتم ربط مؤسسة بعد.</p>
        )}
      </section>

      <section className="card" style={{ padding: 22 }}>
        <div className="sec-title">
          <h2>الوصول الحالي</h2>
          <span className="meta">{enabledCount} مجالات مفعّلة</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(220px,0.9fr) minmax(280px,1.4fr)',
            gap: 18,
          }}
        >
          <div className="card" style={{ padding: 16, background: 'var(--cream)' }}>
            <span className="lbl">الدور الحالي</span>
            <h3 className="h-section" style={{ marginTop: 8 }}>
              {roleLabel}
            </h3>
            <p className="body" style={{ marginTop: 8 }}>
              هذه الصفحة تعرض ملخص الوصول فقط. تعديل الأدوار والصلاحيات سيتم من صفحة الفريق عندما
              تكتمل واجهتها.
            </p>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))',
              gap: 10,
            }}
          >
            {visibleGroups.map((group) => (
              <div
                key={group.title}
                className="card"
                style={{
                  padding: 14,
                  borderColor: group.enabled ? 'rgba(107,174,117,.35)' : 'var(--line-2)',
                  background: group.enabled ? 'var(--paper)' : 'rgba(255,253,248,.55)',
                }}
              >
                <span
                  className={group.enabled ? 'pill pill-success' : 'pill'}
                  style={{ marginBottom: 10 }}
                >
                  {group.enabled ? 'مفعّل' : 'غير متاح'}
                </span>
                <h3 className="h-section" style={{ fontSize: 14 }}>
                  {group.title}
                </h3>
                <p className="tiny" style={{ marginTop: 6 }}>
                  {group.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Task 300A — this promised plan and subscription management as
          future work. `/app/billing` already shows all of it, and
          self-serve management of any of it is not scheduled at all
          (CLP-1.2). */}
      <Placeholder
        title="اشتراك الوكالة وإعدادات المؤسسة"
        description="بيانات اشتراك الوكالة لدى Super Agency — الخطة وحدودها والاستهلاك وما تشمله — معروضة للقراءة في صفحة «اشتراك الوكالة». تغيير الخطة أو تسجيل دفعة يتم عبر فريق Super Agency، ولا يمكن تنفيذ أيٍّ منهما ذاتياً داخل المنتج."
      />
    </div>
  );
}

function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}

function roleTitle(key: string): string {
  switch (key) {
    case 'owner':
      return 'مالك الوكالة';
    case 'marketing_manager':
      return 'مدير تسويق';
    case 'account_manager':
      return 'مدير حسابات';
    case 'media_buyer':
      return 'Media buyer';
    case 'client_viewer':
      return 'عميل - مشاهدة';
    case 'client_approver':
      return 'عميل - موافقات';
    default:
      return key.replaceAll('_', ' ');
  }
}
